import React, { FC, useCallback, useRef, useState } from 'react';
import { AVideo } from '../../atoms/AVideo/index';
import { type BrightcovePlayerRef } from '../../atoms/AVideo/AVideo.types';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import { MButtonOverlay } from '../MButtonOverlay/index';
import { isBrightcove } from '../../utils/videoUtils';
import * as Styled from './MVideoBackground.styles';
import type { MVideoBackgroundProps } from './MVideoBackground.types';
import {
  MVideoBackgroundButtonPosition,
  MVideoBackgroundErrorMessage,
  MVideoBackgroundAriaLabel,
} from './MVideoBackground.types';
import type { MButtonOverlayProps } from '../MButtonOverlay/MButtonOverlay.types';

const COMPONENT_NAME = 'm-video-background';
const TEST_ID = 'web-ui-kit-MVideoBackground';

const DEFAULT_BUTTON_OVERLAY: MButtonOverlayProps = {
  iconOnly: true,
  imageOverlay: true,
  aIcon: { icon: 'icon-pause', size: AIconSize.L },
};

interface ErrorWithMessage {
  message: string;
}

const isErrorWithMessage = (error: unknown): error is ErrorWithMessage =>
  typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string';

const getErrorMessage = (error: unknown): string =>
  isErrorWithMessage(error) ? error.message : MVideoBackgroundErrorMessage.Default;

export const MVideoBackground: FC<MVideoBackgroundProps> = ({
  buttonPosition = MVideoBackgroundButtonPosition.Bottom,
  aVideo,
  mButtonOverlay,
  className,
  ...nativeProps
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const playHandlerRef = useRef<(() => void) | null>(null);
  const pauseHandlerRef = useRef<(() => void) | null>(null);
  const playerRef = useRef<BrightcovePlayerRef | null>(null);

  const handlePlayerError = useCallback((error?: unknown) => {
    setErrorMessage(getErrorMessage(error));
  }, []);

  const handlePlayerReady = useCallback((player: BrightcovePlayerRef) => {
    playerRef.current = player;

    if (typeof player.on === 'function') {
      if (typeof player.off === 'function') {
        if (playHandlerRef.current) player.off('play', playHandlerRef.current);
        if (pauseHandlerRef.current) player.off('pause', pauseHandlerRef.current);
      }

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);

      playHandlerRef.current = handlePlay;
      pauseHandlerRef.current = handlePause;

      player.on('play', handlePlay);
      player.on('pause', handlePause);
    }

    // Ensure autoplay starts — browsers may block if not muted
    const playResult = player.play?.();
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => {
        setIsPlaying(false);
      });
    }
  }, []);

  const handleTogglePlay = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const buttonProps = mButtonOverlay ?? DEFAULT_BUTTON_OVERLAY;

  return (
    <Styled.Wrapper
      {...nativeProps}
      $aspectRatio={aVideo.aspectRatio}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={className}
    >
      <Styled.VideoContainer>
        <AVideo
          {...aVideo}
          autoPlay={true}
          muted={true}
          loop={true}
          controls={false}
          isPlaying={isPlaying}
          {...(isBrightcove(aVideo) && {
            onPlayerReady: handlePlayerReady,
            onPlayerError: handlePlayerError,
          })}
        />
      </Styled.VideoContainer>

      {errorMessage && (
        <Styled.ErrorOverlay role="alert" aria-live="assertive" aria-atomic="true">
          <Styled.ErrorText>{errorMessage}</Styled.ErrorText>
        </Styled.ErrorOverlay>
      )}

      {!errorMessage && (
        <Styled.OverlayContainer $buttonPosition={buttonPosition}>
          <MButtonOverlay
            {...buttonProps}
            imageOverlay={true}
            aria-label={isPlaying ? MVideoBackgroundAriaLabel.Pause : MVideoBackgroundAriaLabel.Play}
            aIcon={{
              ...buttonProps.aIcon,
              icon: isPlaying ? 'icon-pause' : 'icon-play',
              size: buttonProps.aIcon?.size ?? AIconSize.L,
            }}
            onClick={handleTogglePlay}
          />
        </Styled.OverlayContainer>
      )}
    </Styled.Wrapper>
  );
};

export * from './MVideoBackground.types';
